import { useState } from "react";
import { toast } from "sonner";
import { Mail, MapPin, Instagram, Youtube, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import { TiktokIcon } from "@/components/site/BrandIcons";
import { FacebookIconMenu, FacebookCommunityBlock } from "@/components/site/FacebookMenu";

const initial = { name: "", email: "", subject: "", message: "" };

export default function ContactSection() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  async function onSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent. A sister will be in touch soon.");
      setForm(initial);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail?.[0]?.msg ||
          err?.response?.data?.detail ||
          "Could not send message. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section
      id="contact"
      className="relative py-24 sm:py-32 border-t border-white/5"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5 space-y-10">
          <div>
            <div className="text-xs uppercase tracking-[0.35em] text-brand mb-4 flex items-center gap-3">
              <span className="h-px w-10 bg-brand" /> Contact
            </div>
            <h2
              className="text-4xl sm:text-5xl lg:text-6xl font-serif leading-[1.02]"
              data-testid="contact-title"
            >
              Drop a line
              <br />
              <span className="italic text-brand">to the crown.</span>
            </h2>
            <p className="text-white/65 mt-6 max-w-md leading-relaxed">
              Press inquiries, event partnerships, or just wanna show the
              sisterhood some love? We read every message.
            </p>
          </div>

          <div className="space-y-5">
            <ContactLine
              icon={Mail}
              label="MC Email"
              value={BRAND.emails.mc}
              href={`mailto:${BRAND.emails.mc}`}
              testId="contact-email-mc"
            />
            <ContactLine
              icon={Mail}
              label="SC Email"
              value={BRAND.emails.sc}
              href={`mailto:${BRAND.emails.sc}`}
              testId="contact-email-sc"
            />
            <ContactLine
              icon={MapPin}
              label="Based In · Ridden Nationwide"
              value="Houston, Texas · USA"
              testId="contact-location"
            />
          </div>

          <div className="flex gap-3" data-testid="contact-socials">
            <a
              href={BRAND.social.instagram}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center w-11 h-11 border border-white/15 text-white/70 hover:border-brand hover:text-brand transition-colors"
              aria-label="Instagram"
              data-testid="contact-social-ig"
            >
              <Instagram className="w-4 h-4" />
            </a>
            <FacebookIconMenu
              testIdPrefix="contact-fb"
              className="w-auto h-11 px-3 border border-white/15 text-white/70 hover:border-brand hover:text-brand transition-colors"
              align="start"
            />
            <a
              href={BRAND.social.tiktok}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center w-11 h-11 border border-white/15 text-white/70 hover:border-brand hover:text-brand transition-colors"
              aria-label="TikTok"
              data-testid="contact-social-tt"
            >
              <TiktokIcon className="w-4 h-4" />
            </a>
            <a
              href={BRAND.social.youtube}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center w-11 h-11 border border-white/15 text-white/70 hover:border-brand hover:text-brand transition-colors"
              aria-label="YouTube"
              data-testid="contact-social-yt"
            >
              <Youtube className="w-4 h-4" />
            </a>
          </div>

          <FacebookCommunityBlock />
        </div>

        <div className="lg:col-span-7">
          <form
            onSubmit={onSubmit}
            className="bg-[#0a0a0a] border border-white/10 p-6 sm:p-10 space-y-5"
            data-testid="contact-form"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.25em] text-white/70">
                  Name *
                </Label>
                <Input
                  required
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Your name"
                  className="bg-[#050505] border-white/15 rounded-none h-12 focus-visible:ring-brand focus-visible:ring-1 focus-visible:border-brand text-white"
                  data-testid="contact-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.25em] text-white/70">
                  Email *
                </Label>
                <Input
                  required
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@email.com"
                  className="bg-[#050505] border-white/15 rounded-none h-12 focus-visible:ring-brand focus-visible:ring-1 focus-visible:border-brand text-white"
                  data-testid="contact-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.25em] text-white/70">
                Subject *
              </Label>
              <Input
                required
                value={form.subject}
                onChange={update("subject")}
                placeholder="What's this about?"
                className="bg-[#050505] border-white/15 rounded-none h-12 focus-visible:ring-brand focus-visible:ring-1 focus-visible:border-brand text-white"
                data-testid="contact-subject-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.25em] text-white/70">
                Message *
              </Label>
              <Textarea
                required
                rows={6}
                value={form.message}
                onChange={update("message")}
                placeholder="Write your message to the Crown Jewels sisterhood..."
                className="bg-[#050505] border-white/15 rounded-none focus-visible:ring-brand focus-visible:ring-1 focus-visible:border-brand text-white resize-none"
                data-testid="contact-message-input"
              />
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full sm:w-auto h-14 px-10 bg-brand hover:bg-brand-hover text-white rounded-none text-sm font-semibold uppercase tracking-[0.2em] shadow-brand group"
              data-testid="contact-submit-btn"
            >
              {submitting ? "Sending..." : "Send Message"}
              <Send className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
}

function ContactLine({ icon: Icon, label, value, href, testId }) {
  const Body = (
    <div className="flex items-start gap-4 group">
      <span className="inline-flex items-center justify-center w-11 h-11 border border-white/15 group-hover:border-brand transition-colors shrink-0">
        <Icon className="w-4 h-4 text-brand" />
      </span>
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-white/50">
          {label}
        </div>
        <div className="text-white group-hover:text-brand transition-colors break-all">
          {value}
        </div>
      </div>
    </div>
  );
  return href ? (
    <a href={href} data-testid={testId}>
      {Body}
    </a>
  ) : (
    <div data-testid={testId}>{Body}</div>
  );
}
